import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {getNeighborHexKeys} from "../botGrid";
import {classifyOwnCity} from "../strategy/bonusAxis";
import {checkIfCityHasWorkingArsenal} from "../../gameState/recruitment/checkIfCityHasWorkingArsenal";

// Волна 33: чистые функции стройки «сильных городов» бонусного бота
// (см. configs/botBonusBehavior и strategy/bonusAxis). Вынесены из
// decideConstructionStep, чтобы правила про город кандидата, тыловой хаб и
// цепочку снабжения проверялись юнит-тестами без стора.
//
// Главная грабля, которую закрывает модуль: citiesCache внутри фазы стройки
// не обновляется, и правила «одна ферма/казарма на город» текли между тиками
// (pending-постройки не видны). Поэтому город кандидата здесь определяется по
// future-кэшу городов (hexes + pending) плюс синтетические города для ЦК,
// запланированных в ТЕКУЩЕЙ цепочке, а строения считаются по virtual.hexes.

/**
 * Множитель нового города по BFS-дистанции до ближайшего своего
 * сформированного отряда: дальше soft — softFactor, дальше hard (или вне
 * лимита BFS) — hardFactor.
 */
export const calcNewCityDistanceFactor = (distance, bonusProfile, enemyDistance = 0) => {
  // Вне зоны риска (глубокий тыл) штраф не действует: такие города бот почти
  // не теряет, а очки они приносят (см. комментарий в configs/botBonusBehavior)
  if (enemyDistance > bonusProfile.newCityRiskZoneDistance) {
    return 1;
  }
  if (distance > bonusProfile.newCityUnitDistanceHard) {
    return bonusProfile.newCityUnitDistanceHardFactor;
  }
  if (distance > bonusProfile.newCityUnitDistanceSoft) {
    return bonusProfile.newCityUnitDistanceSoftFactor;
  }
  return 1;
};

/**
 * distanceFactor(cellKey) для контекста скоринга. ownFormedUnitDistances —
 * Map BFS от ячеек своих отрядов size ≥ 3 (лимит 8) либо null: без
 * сформированных отрядов ранняя экспансия идет как раньше (множитель 1).
 * enemyQuarterDistances — Map BFS от кварталов ЖИВЫХ противников: вне зоны
 * риска штраф не применяется (см. calcNewCityDistanceFactor).
 */
export const createDistanceFactor = (ownFormedUnitDistances, bonusProfile, enemyQuarterDistances) => (cellKey) => {
  if (!ownFormedUnitDistances) {
    return 1;
  }
  const enemyDistance = enemyQuarterDistances ? (enemyQuarterDistances.get(cellKey) ?? Infinity) : 0;
  return calcNewCityDistanceFactor(
    ownFormedUnitDistances.get(cellKey) ?? Infinity, bonusProfile, enemyDistance
  );
};

/**
 * Города бота на этот тик: future-кэш (реальные + pending) плюс синтетические
 * {centralQuarter, quarter: []} для ЦК, запланированных в текущей цепочке
 * (их еще нет ни в hexes, ни в pending).
 */
export const buildFutureOwnCities = (futureOwnCities, virtualCentralQuarters) => {
  const cities = [...futureOwnCities];
  const knownKeys = new Set(cities.map((city) => city.centralQuarter));
  virtualCentralQuarters.forEach((centralQuarterKey) => {
    if (!knownKeys.has(centralQuarterKey)) {
      cities.push({centralQuarter: centralQuarterKey, quarter: [], isSynthetic: true});
    }
  });
  return cities;
};

/**
 * Индекс «квартал → город» и «город → кварталы». Кварталы future-города
 * приписаны ему; кварталы из virtual.allQuarters без города (базовые
 * кварталы текущей цепочки) — к городу смежного ЦК (правило размещения:
 * базовый квартал всегда смежен центральному), иначе к городу любого
 * смежного уже приписанного квартала.
 */
export const buildCityQuarterIndex = (cities, virtualAllQuarters, gridRenderer) => {
  const cityByQuarterKey = new Map();
  const quartersByCity = new Map();
  const assign = (quarterKey, city) => {
    cityByQuarterKey.set(quarterKey, city);
    if (!quartersByCity.has(city)) {
      quartersByCity.set(city, []);
    }
    quartersByCity.get(city).push(quarterKey);
  };
  cities.forEach((city) => {
    getAllQuartersOfCity(city).forEach((quarterKey) => {
      if (!cityByQuarterKey.has(quarterKey)) {
        assign(quarterKey, city);
      }
    });
  });

  const findOwner = (quarterKey) => {
    const neighbors = getNeighborHexKeys(quarterKey, gridRenderer);
    for (const adjKey of neighbors) {
      const adjCity = cityByQuarterKey.get(adjKey);
      if (adjCity && adjCity.centralQuarter === adjKey) {
        return adjCity;
      }
    }
    for (const adjKey of neighbors) {
      const adjCity = cityByQuarterKey.get(adjKey);
      if (adjCity) {
        return adjCity;
      }
    }
    return null;
  };
  let unassigned = [...virtualAllQuarters].filter((quarterKey) => !cityByQuarterKey.has(quarterKey));
  let hasProgress = true;
  while (unassigned.length > 0 && hasProgress) {
    const rest = [];
    for (const quarterKey of unassigned) {
      const owner = findOwner(quarterKey);
      if (owner) {
        assign(quarterKey, owner);
      } else {
        rest.push(quarterKey);
      }
    }
    hasProgress = rest.length < unassigned.length;
    unassigned = rest;
  }
  return {cityByQuarterKey, quartersByCity};
};

/**
 * Ячейки, смежные хотя бы одному кварталу города (без дублей).
 */
export const collectCityAdjacentCells = (cityQuarters, gridRenderer) => {
  const cells = new Set();
  cityQuarters.forEach((quarterKey) => {
    getNeighborHexKeys(quarterKey, gridRenderer).forEach((adjKey) => cells.add(adjKey));
  });
  return cells;
};

/**
 * Число ячеек, смежных кварталам города, с этим строением в virtual.hexes
 * (реальные + pending + запланированные в цепочке).
 */
export const countCityAdjacentTokens = (cityQuarters, token, virtualHexes, gridRenderer) => {
  let count = 0;
  collectCityAdjacentCells(cityQuarters, gridRenderer).forEach((cellKey) => {
    if (virtualHexes[cellKey] === token) {
      count += 1;
    }
  });
  return count;
};

const checkIfWarehouseAt = (hexKey, specialBuildings, plannedWarehouses) => (
  specialBuildings[hexKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
  || Boolean(plannedWarehouses && plannedWarehouses.has(hexKey))
);

/**
 * Суммарная емкость ферм города (единиц снабжения): 2 у фермы, смежной
 * реальному или запланированному в цепочке складу, иначе 1.
 */
export const calcCityFarmCapacity = (cityQuarters, virtualHexes, specialBuildings, plannedWarehouses, gridRenderer) => {
  let capacity = 0;
  collectCityAdjacentCells(cityQuarters, gridRenderer).forEach((cellKey) => {
    if (virtualHexes[cellKey] !== COMMON_BUILDING_TOKENS.FARM) {
      return;
    }
    const hasWarehouse = getNeighborHexKeys(cellKey, gridRenderer).some(
      (adjKey) => checkIfWarehouseAt(adjKey, specialBuildings, plannedWarehouses)
    );
    capacity += hasWarehouse ? 2 : 1;
  });
  return capacity;
};

const countAdjacentFarms = (hexKey, virtualHexes, gridRenderer) => getNeighborHexKeys(hexKey, gridRenderer)
  .filter((adjKey) => virtualHexes[adjKey] === COMMON_BUILDING_TOKENS.FARM).length;

/**
 * Квартал-хаб тылового города — где стоит или встанет склад: базовый
 * квартал без спецстроения либо со складом, с максимумом смежных ферм по
 * virtual.hexes (равенство — первый по порядку). Без подходящего базового
 * квартала хабом считается центральный: первые фермы тыла ставятся к нему,
 * а квартал-платформа под склад рядом с ними подтянется правилом C2.
 */
export const pickRearHubQuarter = (city, cityQuarters, specialBuildings, virtualHexes, gridRenderer) => {
  let best = null;
  let bestFarms = -1;
  cityQuarters.forEach((quarterKey) => {
    if (quarterKey === city.centralQuarter) {
      return;
    }
    const special = specialBuildings[quarterKey];
    if (special && special !== SPECIAL_BUILDING_TOKENS.WAREHOUSE) {
      return;
    }
    const farms = countAdjacentFarms(quarterKey, virtualHexes, gridRenderer);
    if (farms > bestFarms) {
      best = quarterKey;
      bestFarms = farms;
    }
  });
  return best || city.centralQuarter;
};

/**
 * Пустые ячейки на кратчайших путях «фермы тыла → кварталы кузницы»:
 * dRear — BFS от ферм тылового хаба, dStaging — BFS от кварталов кузницы,
 * D — минимум dRear по кварталам кузницы. Ячейка на пути, если
 * dRear + dStaging === D. Дорога на такой ячейке делает ее непустой, и
 * цепочка снабжения из тыла доходит до кузницы.
 */
export const findSupplyChainPathCells = (rearDistances, stagingDistances, stagingQuarterKeys, hexes) => {
  const cells = new Set();
  let targetDistance = Infinity;
  stagingQuarterKeys.forEach((quarterKey) => {
    const distance = rearDistances.get(quarterKey);
    if (distance !== undefined && distance < targetDistance) {
      targetDistance = distance;
    }
  });
  if (!Number.isFinite(targetDistance)) {
    return cells;
  }
  stagingDistances.forEach((stagingDistance, cellKey) => {
    if (hexes[cellKey]) {
      return;
    }
    const rearDistance = rearDistances.get(cellKey);
    if (rearDistance !== undefined && rearDistance + stagingDistance === targetDistance) {
      cells.add(cellKey);
    }
  });
  return cells;
};

/**
 * Индекс городов бота на одну итерацию плана цепочки (virtual меняется с
 * каждым шагом, поэтому пересобирается вместе с e32-мемо). Все ответы
 * мемоизируются по городу.
 *
 *  - futureOwnCities — города из future-кэша (hexes + pending);
 *  - virtual — виртуальное состояние цепочки (hexes, centralQuarters,
 *    allQuarters, plannedWarehouses);
 *  - axis — ось удара; enemyQuarterDistances — карта BFS от кварталов живых
 *    противников для классификации (стройка передает realEnemyQuarterDistances);
 *  - hexes, units, currentPlayerId, buildingsDamage — реальное поле для проверки
 *    «рабочего» арсенала (поврежденный или блокированный чужим отрядом не
 *    считается — тем же предикатом, что найм).
 */
export const createBonusCityIndex = ({
  futureOwnCities, virtual, axis, enemyQuarterDistances, specialBuildings, gridRenderer,
  hexes = virtual.hexes, units = [], currentPlayerId = null, buildingsDamage = {},
}) => {
  const cities = buildFutureOwnCities(futureOwnCities, virtual.centralQuarters);
  const {cityByQuarterKey, quartersByCity} = buildCityQuarterIndex(cities, virtual.allQuarters, gridRenderer);
  const classMemo = new Map();
  const countMemo = new Map();
  const distances = enemyQuarterDistances || axis.enemyQuarterDistances;

  const getCityQuarters = (city) => quartersByCity.get(city) || getAllQuartersOfCity(city);

  const classifyCity = (city) => {
    if (!classMemo.has(city.centralQuarter)) {
      classMemo.set(city.centralQuarter, classifyOwnCity(city, axis, distances));
    }
    return classMemo.get(city.centralQuarter);
  };

  const countTokens = (city, token) => {
    const memoKey = `${city.centralQuarter}|${token}`;
    if (!countMemo.has(memoKey)) {
      countMemo.set(memoKey, countCityAdjacentTokens(getCityQuarters(city), token, virtual.hexes, gridRenderer));
    }
    return countMemo.get(memoKey);
  };

  return {
    cities,
    getCityByQuarterKey: (quarterKey) => cityByQuarterKey.get(quarterKey) || null,
    getCityQuarters,
    // Число базовых кварталов города с учетом pending и цепочки
    countBaseQuarters: (city) => getCityQuarters(city).length - 1,
    /**
     * Город кандидата фермы/казармы по смежным кварталам: фронтирный
     * предпочитается тыловому (ферма у границы двух городов — фронтирная)
     */
    getCandidateCity: (adjacentKeys) => {
      let first = null;
      for (const adjKey of adjacentKeys) {
        const city = cityByQuarterKey.get(adjKey);
        if (!city) {
          continue;
        }
        if (classifyCity(city) === 'frontier') {
          return city;
        }
        if (!first) {
          first = city;
        }
      }
      return first;
    },
    classifyCity,
    countCityAdjacentTokens: countTokens,
    isStagingCity: (city) => Boolean(city) && city.centralQuarter === axis.stagingCityKey,
    // Кузница, ОТДЕЛЕННАЯ от тыла: при одном городе staging = rear, и правила
    // кластера казарм (C4) там не действуют — цепочки ферм из тыла нет
    isForgeCity: (city) => Boolean(city)
      && city.centralQuarter === axis.stagingCityKey
      && axis.stagingCityKey !== axis.rearCityKey,
    calcCityFarmCapacity: (city) => calcCityFarmCapacity(
      getCityQuarters(city), virtual.hexes, specialBuildings, virtual.plannedWarehouses, gridRenderer
    ),
    pickRearHubQuarter: (city) => pickRearHubQuarter(
      city, getCityQuarters(city), specialBuildings, virtual.hexes, gridRenderer
    ),
    hasWarehouse: (city) => getCityQuarters(city).some(
      (quarterKey) => checkIfWarehouseAt(quarterKey, specialBuildings, virtual.plannedWarehouses)
    ),
    // Рабочий арсенал: есть и не поврежден/не блокирован чужим отрядом
    hasArsenal: (city) => checkIfCityHasWorkingArsenal(
      city, currentPlayerId, units, hexes, specialBuildings, buildingsDamage
    ),
    // Все базовые кварталы города (включая цепочку) заняты спецстроениями
    areBaseQuartersOccupied: (city) => getCityQuarters(city).every(
      (quarterKey) => quarterKey === city.centralQuarter || Boolean(specialBuildings[quarterKey])
    ),
  };
};
